import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import { MessageCircle, Share2, Music, FileText, Download, ThumbsUp, X, Play, Film, MoreHorizontal, Repeat2, Link2, UserMinus, UserPlus, Ban, Flag, Trash2, Volume2, VolumeX, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { fileUrl } from "@/lib/uploads";
import { BlurImage } from "@/lib/blur";
import LinkifiedText from "@/components/chat/LinkifiedText";
import MediaViewer from "@/components/chat/MediaViewer";
import CommentList from "@/components/social/CommentList";
import { REACTIONS, emojiFor, topReactionEmojis } from "@/components/social/reactions";
import { getInitials, avatarColor } from "@/lib/avatar";
import LiveRing from "@/components/live/LiveRing";
import ProductRefCard from "@/components/shared/ProductRefCard";
import ShareSheet from "@/components/shared/ShareSheet";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { enqueueView, VIEW_VISIBILITY_RATIO, VIEW_DWELL_MS } from "@/lib/viewTracker";
import { toast } from "sonner";

const viewedThisSession = new Set();
const REPORT_REASONS = [
  { key: "Spam", emoji: "🚫", label: "s_reason_spam" },
  { key: "Harassment or bullying", emoji: "😠", label: "s_reason_harassment" },
  { key: "Nudity or sexual content", emoji: "🔞", label: "s_reason_nudity" },
  { key: "Scam or fraud", emoji: "⚠️", label: "s_reason_scam" },
  { key: "Something else", emoji: "🚩", label: "s_reason_other" },
];
const fmtCount = (n) => (n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, "") + "K" : String(n || 0));

function Avatar({ name, src, size = 44, onClick, uid }) {
  const btn = (
    <button onClick={onClick} className="rounded-2xl grid place-items-center text-[var(--rc-gold)] font-semibold border-2 border-[var(--rc-gold)] overflow-hidden shrink-0"
      style={{ height: size, width: size, backgroundColor: src ? "var(--rc-bg)" : avatarColor(name) }}>
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : getInitials(name)}
    </button>
  );
  return uid ? <LiveRing uid={uid}>{btn}</LiveRing> : btn;
}

function DocThumb({ url }) {
  const [err, setErr] = useState(false);
  if (err) return <div className="h-12 w-12 shrink-0 grid place-items-center rounded-lg bg-[var(--rc-surface)]"><FileText size={20} className="text-[var(--rc-gold)]" /></div>;
  return <img src={fileUrl(url + "/thumb")} alt="" onError={() => setErr(true)} className="h-12 w-12 shrink-0 rounded-lg object-cover bg-[var(--rc-surface)]" />;
}

// A single photo is never TALLER than a 16:9 video. Wider landscape keeps its
// natural (shorter) height; anything taller (square/portrait) is shown smaller &
// centred on a soft blurred backdrop — never cropped.
function SinglePhoto({ m, onOpen, testid }) {
  const stored = (m.w && m.h) ? m.w / m.h : ((m.media_w && m.media_h) ? m.media_w / m.media_h : null);
  const [ratio, setRatio] = useState(stored);
  const natural = ratio || 1.2;
  const ar = Math.max(natural, 16 / 9); // cap height at the 16:9 height
  const contain = natural < 16 / 9 - 0.001;
  return (
    <button onClick={onOpen} data-testid={testid}
      className="relative block w-full rounded-2xl overflow-hidden border border-[var(--rc-border)] bg-[var(--rc-bg)]"
      style={{ aspectRatio: String(ar), maxHeight: "min(58vh, 480px)" }}>
      {contain && <img src={fileUrl(m.url)} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-60" />}
      <BlurImage src={fileUrl(m.url)} hash={m.blur_hash} className="relative w-full h-full" imgClassName={`w-full h-full ${contain ? "object-contain" : "object-cover"}`}
        onNaturalSize={(w, h) => { if (!stored && h) setRatio(w / h); }} />
    </button>
  );
}

// A single video autoplays muted (looping, inline) when scrolled into view, like X.
function InlineVideo({ m, onOpen, onOpenReel, testid }) {
  const ref = useRef(null);
  const stored = (m.w && m.h) ? m.w / m.h : ((m.media_w && m.media_h) ? m.media_w / m.media_h : null);
  const [ratio, setRatio] = useState(stored);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const natural = ratio || 1.2;
  const ar = Math.max(natural, 16 / 9); // never taller than 16:9
  const contain = natural < 16 / 9 - 0.001;

  useEffect(() => { if (ref.current) ref.current.muted = muted; }, [muted]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting && e.intersectionRatio >= 0.6) {
        el.play().then(() => setPlaying(true)).catch(() => {});
      } else {
        el.pause();
        setPlaying(false);
      }
    }, { threshold: [0, 0.6, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div className="relative block w-full rounded-2xl overflow-hidden border border-[var(--rc-border)] bg-black"
      style={{ aspectRatio: String(ar), maxHeight: "min(58vh, 480px)" }} data-testid={testid}>
      {contain && m.poster && <img src={fileUrl(m.poster)} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-50" />}
      <video ref={ref} src={fileUrl(m.url)} poster={m.poster ? fileUrl(m.poster) : undefined}
        loop playsInline muted preload="metadata"
        onLoadedMetadata={(e) => { if (!stored && e.target.videoWidth) setRatio(e.target.videoWidth / e.target.videoHeight); }}
        onClick={onOpenReel || onOpen}
        className={`relative w-full h-full ${contain ? "object-contain" : "object-cover"} cursor-pointer`} />
      {!playing && (
        <span className="absolute inset-0 grid place-items-center pointer-events-none">
          <span className="h-14 w-14 grid place-items-center rounded-full bg-black/55"><Play size={26} className="text-white ml-1" fill="white" /></span>
        </span>
      )}
      <button onClick={(e) => { e.stopPropagation(); setMuted((x) => !x); }} data-testid={`${testid}-mute`}
        className="absolute bottom-2 right-2 h-9 w-9 grid place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm">
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
    </div>
  );
}

// Visual media grid (images + videos). Tapping any cell opens the fullscreen viewer.
function MediaGrid({ media, testPrefix, onOpen, onOpenReel }) {
  const visual = media.filter((m) => m.type === "image" || m.type === "video");
  if (!visual.length) return null;
  if (visual.length === 1 && visual[0].type === "image") {
    return (
      <div className="px-4 pt-1">
        <SinglePhoto m={visual[0]} onOpen={() => onOpen(0)} testid={`${testPrefix}-img-0`} />
      </div>
    );
  }
  if (visual.length === 1 && visual[0].type === "video") {
    return (
      <div className="px-4 pt-1">
        <InlineVideo m={visual[0]} onOpen={() => onOpen(0)} onOpenReel={onOpenReel ? () => onOpenReel(visual[0].url) : undefined} testid={`${testPrefix}-video-0`} />
      </div>
    );
  }
  return (
    <div className="px-4 pt-1">
      <div className={`grid gap-0.5 rounded-2xl overflow-hidden border border-[var(--rc-border)] ${visual.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
        {visual.slice(0, 4).map((m, i) => (
          <button key={i} onClick={() => (m.type === "video" && onOpenReel ? onOpenReel(m.url) : onOpen(i))} className="relative aspect-square bg-[var(--rc-bg)]"
            data-testid={m.type === "video" ? `${testPrefix}-video-${i}` : `${testPrefix}-img-${i}`}>
            {m.type === "image"
              ? <BlurImage src={fileUrl(m.url)} hash={m.blur_hash} className="h-full w-full" imgClassName="h-full w-full object-cover" />
              : m.poster
                ? <BlurImage src={fileUrl(m.poster)} hash={m.blur_hash} className="h-full w-full" imgClassName="h-full w-full object-cover" />
                : <div className="h-full w-full grid place-items-center bg-gradient-to-br from-[var(--rc-bg)] to-black"><Film size={30} className="text-[var(--rc-muted)]" /></div>}
            {m.type === "video" && (
              <span className="absolute inset-0 grid place-items-center">
                <span className="h-14 w-14 grid place-items-center rounded-full bg-black/55"><Play size={26} className="text-white ml-1" fill="white" /></span>
              </span>
            )}
            {i === 3 && visual.length > 4 && (
              <span className="absolute inset-0 grid place-items-center bg-black/55 text-white text-2xl font-semibold">+{visual.length - 4}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PostCard({ post, onOpenProfile, onDeleted, onHideAuthor, onOpenReel, onEngage, onNotInterested, onOpenDetail, forceComments, stickyComments }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [p, setP] = useState(post);
  const [showComments, setShowComments] = useState(!!forceComments);
  const [picker, setPicker] = useState(false);
  const [showReactors, setShowReactors] = useState(false);
  const [reactors, setReactors] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [menu, setMenu] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [following, setFollowing] = useState(!!post.am_following);
  const [viewer, setViewer] = useState(null); // {items, idx}
  const cardRef = useRef(null);

  // Count a view only when ~half the post has been on screen for ~1s (visibility + dwell).
  // Thresholds live in viewTracker. Never count the author's own post. Sending is batched
  // and de-duplicated server-side, so this just enqueues once per post per tab.
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !p?.id || p.author_id === user.id || viewedThisSession.has(p.id)) return;
    let dwell = null;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting && e.intersectionRatio >= VIEW_VISIBILITY_RATIO) {
        if (dwell == null) {
          dwell = setTimeout(() => {
            if (viewedThisSession.has(p.id)) return;
            viewedThisSession.add(p.id);
            enqueueView(p.id);
            io.disconnect();
          }, VIEW_DWELL_MS);
        }
      } else if (dwell != null) {
        clearTimeout(dwell); // left the screen before the dwell completed — cancel
        dwell = null;
      }
    }, { threshold: [VIEW_VISIBILITY_RATIO] });
    io.observe(el);
    return () => { if (dwell != null) clearTimeout(dwell); io.disconnect(); };
  }, [p?.id, p.author_id, user.id]);

  const openReactors = async () => {
    setShowReactors(true); setReactors(null);
    try { const { data } = await api.get(`/social/posts/${p.id}/reactions`); setReactors(data); } catch { setReactors([]); }
  };
  const react = async (type) => {
    setPicker(false);
    try { const { data } = await api.post(`/social/posts/${p.id}/react`, { type }); setP(data); onEngage && onEngage("like", p.id); } catch {}
  };
  const quickReact = () => react(p.my_reaction || "like");

  const postLink = `${window.location.origin}/app?post=${p.id}`;
  const share = async () => {
    setMenu(false);
    try {
      if (navigator.share) await navigator.share({ title: "RoyalChat Nova", text: p.text || t("s_check_post"), url: postLink });
      else { await navigator.clipboard.writeText(postLink); toast.success(t("s_link_copied")); }
      const { data } = await api.post(`/social/posts/${p.id}/share`); setP(data); onEngage && onEngage("share", p.id);
    } catch {}
  };
  const copyLink = async () => { setMenu(false); try { await navigator.clipboard.writeText(postLink); toast.success(t("s_link_copied")); } catch {} };
  const reshare = async () => { setMenu(false); try { await api.post(`/social/posts/${p.id}/reshare`); toast.success(t("s_reshared_toast")); } catch {} };
  const del = async () => { setMenu(false); try { await api.delete(`/social/posts/${p.id}`); onDeleted && onDeleted(p.id); toast.success(t("s_deleted")); } catch { toast.error(t("s_could_not_delete")); } };
  const toggleFollow = async () => { setMenu(false); try { const { data } = await api.post(`/social/follow/${p.author_id}`); setFollowing(data.following); toast.success(data.following ? t("s_now_following") : t("s_unfollowed")); } catch {} };
  const block = async () => { setMenu(false); try { await api.post(`/users/${p.author_id}/block`); (onHideAuthor ? onHideAuthor(p.author_id) : onDeleted && onDeleted(p.id)); toast.success(t("s_user_blocked")); } catch {} };
  const report = () => { setMenu(false); setReportOpen(true); };
  const submitReport = async (reason) => {
    setReportOpen(false);
    try { await api.post(`/social/posts/${p.id}/report`, { reason }); onDeleted && onDeleted(p.id); toast.success(t("s_reported")); } catch {}
  };
  const notInterested = async () => {
    setMenu(false);
    if (onNotInterested) { onNotInterested(p); return; }  // parent handles hide + Undo snackbar
    try { await api.post("/social/hide", { post_id: p.id }); onDeleted && onDeleted(p.id); toast.success(t("s_not_interested_toast")); } catch {}
  };
  const muteAuthor = async () => { setMenu(false); try { await api.post("/social/mute", { author_id: p.author_id }); (onHideAuthor ? onHideAuthor(p.author_id) : onDeleted && onDeleted(p.id)); toast.success(`${t("s_muted_toast")} ${p.author_name || ""}`.trim()); } catch {} };

  const mine = p.author_id === user.id;
  const media = p.media || [];
  const audios = media.filter((m) => m.type === "audio");
  const docs = media.filter((m) => m.type === "document");
  const myMeta = REACTIONS.find((r) => r.type === p.my_reaction);
  const total = p.reactions_total || 0;
  const topEmojis = topReactionEmojis(p.reaction_counts, 3);
  const resh = p.reshared;

  const text = p.text || "";
  const LIMIT = 220;
  const isLong = text.length > LIMIT;
  const shownText = expanded || !isLong ? text : text.slice(0, LIMIT).trimEnd() + "… ";

  const openViewerFor = (mediaArr) => (i) => {
    const visual = mediaArr.filter((m) => m.type === "image" || m.type === "video");
    setViewer({ items: visual.map((m) => ({ url: fileUrl(m.url), type: m.type, name: m.name })), idx: i });
  };
  const openReel = onOpenReel ? (url) => onOpenReel(p.id, url) : undefined;
  const openDetail = onOpenDetail ? () => onOpenDetail(p) : null;

  const menuRows = [
    { key: "share", icon: Share2, label: t("s_share_post"), onClick: () => { setMenu(false); setShareOpen(true); } },
    { key: "reshare", icon: Repeat2, label: t("s_reshare"), onClick: reshare },
    { key: "copy", icon: Link2, label: t("s_copy_link"), onClick: copyLink },
    ...(mine
      ? [{ key: "delete", icon: Trash2, label: t("s_delete_post"), onClick: del, danger: true }]
      : [
          { key: "notinterested", icon: EyeOff, label: t("s_not_interested"), onClick: notInterested },
          { key: "mute", icon: VolumeX, label: `${t("s_mute_author")} ${p.author_name || ""}`.trim(), onClick: muteAuthor },
          { key: "follow", icon: following ? UserMinus : UserPlus, label: following ? t("s_unfollow") : t("s_follow"), onClick: toggleFollow },
          { key: "block", icon: Ban, label: t("s_block_user"), onClick: block, danger: true },
          { key: "report", icon: Flag, label: t("s_report_post"), onClick: report, danger: true },
        ]),
  ];

  return (
    <div ref={cardRef} className="bg-[var(--rc-bg)] border-b border-[var(--rc-border)] pb-1" data-testid={`post-${p.id}`}>
      {resh && (
        <div className="flex items-center gap-1.5 px-4 pt-3 text-xs text-[var(--rc-muted)]" data-testid={`post-reshared-tag-${p.id}`}>
          <Repeat2 size={13} /> <span className="font-medium text-[var(--rc-text)]">{p.author_name}</span> {t("s_reshared")}
        </div>
      )}
      <div className="flex items-center gap-3 px-4 pt-3">
        <Avatar name={resh ? resh.author_name : p.author_name} src={resh ? resh.author_avatar : p.author_avatar} uid={resh ? resh.author_id : p.author_id} onClick={() => onOpenProfile && onOpenProfile(resh ? resh.author_id : p.author_id)} />
        <div className="min-w-0 flex-1">
          <button onClick={() => onOpenProfile && onOpenProfile(resh ? resh.author_id : p.author_id)} data-testid={`post-author-${p.id}`} className="font-medium truncate block text-left">{resh ? resh.author_name : p.author_name}</button>
          <div className="flex items-center gap-2">
            <div className="text-xs text-[var(--rc-muted)]" data-testid={`post-time-${p.id}`}>{p.__pinnedNow ? t("s_now") : dayjs(p.created_at).format("DD MMM · HH:mm")}</div>
            {p.topic && (
              <span data-testid={`post-category-${p.id}`}
                className="px-2 py-0.5 rounded-full bg-[var(--rc-gold)]/15 text-[var(--rc-gold)] font-semibold text-[10px] uppercase tracking-wide">
                {t(`topic_${p.topic}`)}
              </span>
            )}
          </div>
        </div>
        <button onClick={() => setMenu(true)} data-testid={`post-menu-${p.id}`} className="text-[var(--rc-muted)] hover:text-[var(--rc-text)] h-9 w-9 grid place-items-center rounded-full"><MoreHorizontal size={20} /></button>
      </div>

      {resh ? (
        <>
          {resh.text && <div className={`px-4 py-2 text-sm whitespace-pre-wrap [overflow-wrap:anywhere] ${openDetail ? "cursor-pointer" : ""}`} onClick={openDetail || undefined}>{resh.text}</div>}
          {resh.product_ref && <div className="px-4 pt-1 pb-2"><ProductRefCard data={resh.product_ref} /></div>}
          <MediaGrid media={resh.media || []} testPrefix={`resh-${p.id}`} onOpen={openDetail ? () => openDetail() : openViewerFor(resh.media || [])} onOpenReel={openDetail ? undefined : openReel} />
        </>
      ) : (
        <>
          {text && (
            <div className={`px-4 py-3 ${openDetail ? "cursor-pointer" : ""}`} onClick={openDetail || undefined} data-testid={`post-open-text-${p.id}`}>
              {!isLong || expanded
                ? <>
                    <LinkifiedText text={text} embedYouTube className="whitespace-pre-wrap [overflow-wrap:anywhere]" />
                    {isLong && expanded && <button onClick={(e) => { e.stopPropagation(); setExpanded(false); }} data-testid={`post-readless-${p.id}`} className="text-[var(--rc-gold)] font-medium mt-1">{t("s_read_less")}</button>}
                  </>
                : <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{shownText}<button onClick={(e) => { e.stopPropagation(); setExpanded(true); }} data-testid={`post-readmore-${p.id}`} className="text-[var(--rc-gold)] font-medium">{t("s_read_more")}</button></p>}
            </div>
          )}
          <MediaGrid media={media} testPrefix={`post-${p.id}`} onOpen={openDetail ? () => openDetail() : openViewerFor(media)} onOpenReel={openDetail ? undefined : openReel} />
          {p.product_ref && <div className="px-4 pt-1 pb-2"><ProductRefCard data={p.product_ref} /></div>}

          {audios.length > 0 && (
            <div className="px-4 py-2 space-y-2">
              {audios.map((m, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`post-audio-${p.id}-${i}`}>
                  <Music size={16} className="text-[var(--rc-gold)] shrink-0" />
                  <audio src={fileUrl(m.url)} controls preload="none" className="flex-1 min-w-0 h-9" />
                </div>
              ))}
            </div>
          )}

          {docs.length > 0 && (
            <div className="px-4 py-2 space-y-2">
              {docs.map((m, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-2" data-testid={`post-doc-${p.id}-${i}`}>
                  <DocThumb url={m.url} />
                  <span className="flex-1 min-w-0 truncate text-sm">{m.name || t("s_document")}</span>
                  <a href={fileUrl(m.url)} target="_blank" rel="noreferrer" download data-testid={`post-doc-open-${p.id}-${i}`}
                    className="h-8 w-8 grid place-items-center rounded-full text-[var(--rc-gold)] hover:bg-[var(--rc-surface)] shrink-0"><Download size={16} /></a>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div className="flex items-center justify-between px-4 pt-3 min-h-[20px]">
        {total > 0 ? (
          <button onClick={openReactors} data-testid={`post-reactions-summary-${p.id}`} className="flex items-center gap-1.5 text-xs text-[var(--rc-muted)] hover:text-[var(--rc-text)]">
            <span className="flex items-center">{topEmojis.map((e, i) => <span key={i} className={`text-sm ${i > 0 ? "-ml-1" : ""}`}>{e}</span>)}</span>
            <span>{total}</span>
          </button>
        ) : <span />}
        {p.views > 0 && <span className="text-xs text-[var(--rc-muted)]" data-testid={`post-views-${p.id}`}>{fmtCount(p.views)} {t("s_views")}</span>}
      </div>

      <div className="flex items-center gap-1 px-2 py-1.5 mt-1 relative">
        {picker && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setPicker(false)} data-testid={`react-backdrop-${p.id}`} />
            <div className="absolute bottom-full left-2 mb-2 z-20 flex items-center gap-1 rounded-full bg-[var(--rc-bg)] border border-[var(--rc-border)] px-2 py-1.5 shadow-xl" data-testid={`react-bar-${p.id}`}>
              {REACTIONS.map((r) => (
                <button key={r.type} onClick={() => react(r.type)} data-testid={`react-${r.type}-${p.id}`} title={r.type}
                  className={`text-2xl leading-none transition-transform hover:scale-125 ${p.my_reaction === r.type ? "scale-110" : ""}`}>{r.emoji}</button>
              ))}
            </div>
          </>
        )}
        <button onClick={quickReact} onContextMenu={(e) => { e.preventDefault(); setPicker(true); }} onDoubleClick={() => setPicker(true)}
          data-testid={`post-like-${p.id}`}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm transition-colors ${myMeta ? "text-[var(--rc-gold)] font-medium" : "text-[var(--rc-muted)]"} hover:bg-[var(--rc-bg)]`}>
          {myMeta ? <span className="text-base leading-none">{myMeta.emoji}</span> : <ThumbsUp size={17} />}
          <span data-testid={`post-like-count-${p.id}`}>{total}</span>
        </button>
        <button onClick={() => setPicker((v) => !v)} data-testid={`post-react-more-${p.id}`}
          className="px-2 py-2 rounded-xl text-xs text-[var(--rc-muted)] hover:bg-[var(--rc-bg)]">😊</button>
        <button onClick={() => (onOpenDetail ? onOpenDetail(p) : setShowComments((v) => !v))} data-testid={`post-comment-${p.id}`} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm text-[var(--rc-muted)] hover:bg-[var(--rc-bg)]">
          <MessageCircle size={17} /> {p.comment_count > 0 && <span>{p.comment_count}</span>}
        </button>
        <button onClick={() => setShareOpen(true)} data-testid={`post-share-${p.id}`} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm text-[var(--rc-muted)] hover:bg-[var(--rc-bg)]">
          <Share2 size={17} /> {p.share_count > 0 && <span>{p.share_count}</span>}
        </button>
      </div>

      {shareOpen && <ShareSheet kind="post" item={p} onClose={() => setShareOpen(false)} />}

      {reportOpen && (
        <>
          <div className="fixed inset-0 z-[70] bg-black/60" onClick={() => setReportOpen(false)} data-testid={`report-backdrop-${p.id}`} />
          <div className="fixed inset-x-0 bottom-0 z-[71] rounded-t-3xl bg-[var(--rc-bg)] border-t border-[var(--rc-border)] p-5 pb-8 max-w-lg mx-auto rc-safe-bottom" data-testid={`report-sheet-${p.id}`}>
            <div className="h-1 w-10 rounded-full bg-[var(--rc-border)] mx-auto mb-4" />
            <h3 className="font-heading text-lg mb-1">{t("s_report_title")}</h3>
            <p className="text-sm text-[var(--rc-muted)] mb-4">{t("s_report_sub")}</p>
            <div className="space-y-2">
              {REPORT_REASONS.map((r) => (
                <button key={r.key} onClick={() => submitReport(r.key)} data-testid={`report-reason-${r.key}-${p.id}`}
                  className="w-full flex items-center gap-3 text-left rounded-xl border border-[var(--rc-border)] px-4 py-3 hover:bg-[var(--rc-surface)] transition-colors">
                  <span className="text-xl leading-none">{r.emoji}</span>
                  <span className="text-sm font-medium">{t(r.label)}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setReportOpen(false)} data-testid={`report-cancel-${p.id}`}
              className="w-full mt-4 rounded-xl py-3 text-sm text-[var(--rc-muted)] border border-[var(--rc-border)]">{t("s_cancel")}</button>
          </div>
        </>
      )}

      {showComments && <CommentList postId={p.id} stickyComposer={stickyComments} onOpenProfile={onOpenProfile} onCountChange={(d) => setP((x) => ({ ...x, comment_count: (x.comment_count || 0) + d }))} />}

      {viewer && <MediaViewer items={viewer.items} startIndex={viewer.idx} onClose={() => setViewer(null)} />}

      {menu && createPortal(
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60" onClick={() => setMenu(false)} data-testid={`post-menu-sheet-${p.id}`}>
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl bg-[var(--rc-surface)] border border-[var(--rc-border)] p-2 pb-4">
            {menuRows.map((row) => (
              <button key={row.key} onClick={row.onClick} data-testid={`post-menu-${row.key}-${p.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl hover:bg-[var(--rc-bg)] text-left ${row.danger ? "text-red-400" : ""}`}>
                <row.icon size={20} /> <span className="text-sm font-medium">{row.label}</span>
              </button>
            ))}
          </div>
        </div>, document.body)}

      {showReactors && createPortal(
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60" onClick={() => setShowReactors(false)} data-testid={`reactors-modal-${p.id}`}>
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-sm max-h-[70vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-[var(--rc-surface)] border border-[var(--rc-border)] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-2">{t("s_reactions")} · {total}
                <span className="flex items-center">{topEmojis.map((e, i) => <span key={i} className={`text-sm ${i > 0 ? "-ml-1" : ""}`}>{e}</span>)}</span>
              </div>
              <button onClick={() => setShowReactors(false)} data-testid={`reactors-close-${p.id}`} className="h-8 w-8 grid place-items-center rounded-full hover:bg-[var(--rc-bg)]"><X size={18} /></button>
            </div>
            {reactors === null ? <div className="text-center py-8 text-[var(--rc-muted)] text-sm">{t("s_loading")}</div>
              : reactors.length === 0 ? <div className="text-center py-8 text-[var(--rc-muted)] text-sm">—</div>
                : <div className="space-y-2.5">
                    {reactors.map((r) => (
                      <div key={r.user_id} className="flex items-center gap-3" data-testid={`reactor-${p.id}-${r.user_id}`}>
                        <div className="relative shrink-0">
                          <Avatar name={r.name} src={r.avatar} size={36} onClick={() => { setShowReactors(false); onOpenProfile && onOpenProfile(r.user_id); }} />
                          <span className="absolute -bottom-1 -right-1 text-sm leading-none">{emojiFor(r.type)}</span>
                        </div>
                        <span className="flex-1 min-w-0 truncate text-sm font-medium">{r.name}</span>
                      </div>
                    ))}
                  </div>}
          </div>
        </div>, document.body)}
    </div>
  );
}
